import React, { useEffect, useRef, useState } from "react";
import { useT } from "../lib/i18n";
import { Sparkle, Heart, ArrowDown } from "@phosphor-icons/react";

const MAX_DOTS = 5;

function relTime(iso, t) {
    if (!iso) return "";
    const ms = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(ms)) return "";
    const s = Math.max(1, Math.floor(ms / 1000));
    if (s < 60) return t("pulse.ago_seconds", { n: s });
    const m = Math.floor(s / 60);
    if (m < 60) return t("pulse.ago_minutes", { n: m });
    const h = Math.floor(m / 60);
    return t("pulse.ago_hours", { n: h });
}

function KindGlyph({ kind }) {
    const cls = "w-6 h-6 rounded-md flex items-center justify-center shrink-0";
    if (kind === "opened") return <div className={`${cls} bg-[#CBA35C] text-[#0A1F16]`}><Sparkle size={13} weight="duotone" /></div>;
    if (kind === "deposit") return <div className={`${cls} bg-[#CBA35C]/20 text-[#CBA35C]`}><ArrowDown size={13} weight="duotone" /></div>;
    return <div className={`${cls} bg-[#D96C4A]/20 text-[#F2A98C]`}><Heart size={13} weight="duotone" /></div>;
}

function formatUSD(n) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
}

/**
 * Vertical "live pulse" rail.
 *
 * Subscribes to the SSE stream `/api/public/recent-deliveries/stream` and
 * shows the last few events as ascending dots. Hover any populated dot to
 * see an anonymized tooltip with the event details — nudges curious
 * visitors into "explore mode" without cluttering the default view.
 *
 * Hidden on small viewports (no room). Falls back silently if SSE isn't
 * reachable. EventSource auto-reconnects on disconnect with backoff.
 */
export default function LivePulseRail() {
    const t = useT();
    const [dots, setDots] = useState([]); // newest at index 0
    const [pulsing, setPulsing] = useState(false);
    const esRef = useRef(null);

    useEffect(() => {
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        if (!base || typeof EventSource === "undefined") return;
        let cancelled = false;
        let reconnectMs = 4000;

        const connect = () => {
            if (cancelled) return;
            const url = `${base}/api/public/recent-deliveries/stream`;
            let es;
            try {
                es = new EventSource(url);
            } catch {
                return;
            }
            esRef.current = es;

            es.addEventListener("pulse", (e) => {
                if (cancelled) return;
                try {
                    const data = JSON.parse(e.data || "{}");
                    setDots((prev) => [{ ...data, _id: `${data.ts}-${Math.random()}` }, ...prev].slice(0, MAX_DOTS));
                    setPulsing(true);
                    setTimeout(() => setPulsing(false), 1200);
                } catch { /* ignore malformed payload */ }
            });

            es.addEventListener("close", () => {
                try { es.close(); } catch { /* noop */ }
                if (!cancelled) setTimeout(connect, 1500);
            });

            es.onerror = () => {
                try { es.close(); } catch { /* noop */ }
                if (cancelled) return;
                setTimeout(connect, reconnectMs);
                reconnectMs = Math.min(reconnectMs * 1.5, 30000);
            };

            es.onopen = () => { reconnectMs = 4000; };
        };

        connect();
        return () => {
            cancelled = true;
            try { esRef.current?.close(); } catch { /* noop */ }
        };
    }, []);

    return (
        <div
            className="hidden lg:flex flex-col items-center w-12 shrink-0 select-none"
            data-testid="live-pulse-rail"
        >
            <p
                className="text-[9px] tracking-[0.3em] uppercase text-white/40 rotate-180"
                style={{ writingMode: "vertical-rl" }}
                aria-hidden
            >
                {t("pulse.label")}
            </p>
            <div className="mt-4 h-64 w-px bg-white/10 relative flex flex-col-reverse items-center justify-start gap-3 py-2">
                {Array.from({ length: MAX_DOTS }).map((_, i) => {
                    const event = dots[i];
                    const isFreshest = i === 0 && pulsing;
                    const opacity = event ? Math.max(0.25, 1 - i * 0.18) : 0.08;
                    const size = event ? (i === 0 ? 12 : 8) : 4;
                    return (
                        <div
                            key={event ? event._id : `empty-${i}`}
                            className="relative group"
                        >
                            <div
                                className={`rounded-full transition-all duration-700 ${
                                    isFreshest ? "ring-4 ring-[#CBA35C]/40 animate-ping-slow" : ""
                                } ${event ? "cursor-pointer" : ""}`}
                                style={{
                                    width: size,
                                    height: size,
                                    background: event ? "#CBA35C" : "rgba(255,255,255,0.4)",
                                    opacity,
                                }}
                                data-testid={event ? `live-pulse-dot-${i}` : undefined}
                            />
                            {event && (
                                <div
                                    className="pointer-events-none absolute right-full top-1/2 -translate-y-1/2 mr-4 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                    data-testid={`live-pulse-tooltip-${i}`}
                                    role="tooltip"
                                >
                                    <div className="rounded-xl bg-[#0F2A1B] border border-white/15 backdrop-blur-md shadow-xl px-3 py-2.5 min-w-[200px]">
                                        <div className="flex items-center gap-2">
                                            <KindGlyph kind={event.kind} />
                                            <p className="text-[12px] font-medium text-[#FBF9F6] whitespace-nowrap">
                                                {t(`pulse.tooltip_${event.kind}`, {
                                                    name: event.name,
                                                    amount: formatUSD(event.amount_bucket),
                                                })}
                                            </p>
                                        </div>
                                        <p className="mt-1 text-[10px] mono tracking-wider text-white/50">
                                            {relTime(event.ts, t)}
                                        </p>
                                    </div>
                                    {/* Right-pointing arrow */}
                                    <div className="absolute left-full top-1/2 -translate-y-1/2 -ml-px w-0 h-0 border-y-4 border-y-transparent border-l-[6px] border-l-[#0F2A1B]" />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            {dots.length > 0 && (
                <p className="mt-3 text-[10px] tracking-widest text-[#CBA35C]/80" data-testid="live-pulse-counter">
                    {t("pulse.recent_count", { n: dots.length })}
                </p>
            )}
        </div>
    );
}

